import React from "react";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import MSWManagementSecondaryCollectionStore from "./store/MSWManagementSecondaryCollectionStore";
import { formattedDate, formattedDateTypeTwo, getCurrentDateTime1 } from "../../../../Common/helper/helpar";

export const MSWManagementVehiclesInnerExport = ({ fromDate, toDate }) => {
  const { MSWManagementSecondaryCollectionInnerReportData } =
    MSWManagementSecondaryCollectionStore();

  const data = MSWManagementSecondaryCollectionInnerReportData || [];
  const currentDateTime = getCurrentDateTime1();
  const reportTitle = `Detailed Vehicle Deployment Data Report as on ` + `(${formattedDateTypeTwo(fromDate)})`;
  const topHeaderText = `Detailed Vehicle Deployment Data Report is generated on (${currentDateTime})`;

  const filteredData = data.filter((row) => {
    if (row.ZoneName === "Total") return false;
    return (
      Number(row.PlannedVehicleCount) > 0 ||
      Number(row.DeployedVehicleCount) > 0 ||
      Number(row.DeploymentPercentage) > 0 ||
      Number(row.HKLVehicleCapacity) > 0
    );
  });

  const totalRow = {
    ZoneName: "Total",
    CircleName: "",
    PlannedVehicleCount: filteredData.reduce((a, b) => a + Number(b.PlannedVehicleCount || 0), 0),
    DeployedVehicleCount: filteredData.reduce((a, b) => a + Number(b.DeployedVehicleCount || 0), 0),
    DeploymentPercentage: (() => {
          const planned = filteredData.reduce(
            (sum, r) => sum + Number(r.PlannedVehicleCount || 0),
            0
          );
          const deployed = filteredData.reduce(
            (sum, r) => sum + Number(r.DeployedVehicleCount || 0),
            0
          );
          return planned > 0
            ? ((deployed / planned) * 100).toFixed(2)
            : "0.00";
        })(),
    HKLVehicleCapacity: filteredData.reduce((a, b) => a + Number(b.HKLVehicleCapacity || 0), 0),
  };

  const finalData = [...filteredData, totalRow];

  const formatDateTime = (date = new Date()) => {
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  };

  /* ================= EXCEL ================= */
  const downloadAsExcel = async () => {
    if (!finalData.length) {
      alert("No data available to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Vehicle Deployment");

    const boldFont = { bold: true };
    const currentDateTime = formatDateTime();

    // Row 1 - Report Generated On
    worksheet.addRow([topHeaderText]);
    worksheet.mergeCells(1, 1, 1, 7);
    worksheet.getCell(1, 1).font = boldFont;
    worksheet.getCell(1, 1).alignment = { horizontal: "center" };

    // Row 2 - Report Title with filter dates
    worksheet.addRow([reportTitle]);
    worksheet.mergeCells(2, 1, 2, 7);
    worksheet.getCell(2, 1).font = boldFont;
    worksheet.getCell(2, 1).alignment = { horizontal: "center" };

    worksheet.addRow([]); // empty row for spacing

    // Header row with background color
    const headerRow = worksheet.addRow([
      "S.No",
      "Corporation",
      "Zone Name",
      "Circle Name",
      "Alloted Vehicles",
      "Deployed Vehicles",
      "Deployment %",
      // "HKL Capacity",
    ]);

    headerRow.eachCell((cell) => {
      cell.font = boldFont;
      cell.alignment = { horizontal: "center", wrapText: true, vertical: "middle" };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "F6B1B1" } }; // light red/pink
    });

    // Data rows
    let serial = 1;
    finalData.forEach((row) => {
      const isTotal = row.ZoneName === "Total";
      const dataRow = worksheet.addRow([
        isTotal ? "" : serial++,
        row.CorporationName ?? "",
        row.ZoneName ?? "",
        row.CircleName ?? "",
        row.PlannedVehicleCount ?? 0,
        row.DeployedVehicleCount ?? 0,
        row.DeploymentPercentage ?? 0,
        // row.HKLVehicleCapacity ?? 0,
      ]);

      dataRow.eachCell((cell) => {
        cell.alignment = { horizontal: "center" };
        if (isTotal) cell.font = boldFont;
      });
    });

    worksheet.columns = Array(7).fill({ width: 20 });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Vehicle_Deployment_Report.xlsx";
    link.click();
  };

  /* ================= PDF ================= */
  const downloadAsPDF = () => {
    if (!finalData.length) {
      alert("No data available to export.");
      return;
    }
    const styledText = (text, x, y, size, options = {}) => {
        const originalSize = doc.getFontSize();
        doc.setFontSize(size);
        doc.text(text, x, y, options);
        doc.setFontSize(originalSize); // Reverts back automatically
    };

    const doc = new jsPDF("l", "mm", "a4");
    const pageWidth = doc.internal.pageSize.width;
    const titleX = pageWidth / 2;
    styledText(topHeaderText, titleX, 15, 12, { align: "center" });
    styledText(reportTitle, titleX, 24, 14, { align: "center" });

    let serial = 1;
    const tableRows = finalData.map((row) => {
      const isTotal = row.ZoneName === "Total";
      return [
        isTotal ? "" : serial++,
        row.CorporationName ?? "",
        row.ZoneName ?? "",
        row.CircleName ?? "",
        row.PlannedVehicleCount ?? 0,
        row.DeployedVehicleCount ?? 0,
        row.DeploymentPercentage ?? 0,
        // row.HKLVehicleCapacity ?? 0,
      ];
    });

    autoTable(doc, {
      startY: 30,
      head: [["S.No","Corporation", "Zone Name", "Circle Name", "Alloted Vehicles", "Deployed Vehicles", "Deployment %",
        //  "HKL Capacity"
        ]],
      body: tableRows,
      theme: "grid",
      headStyles: { fillColor: [0, 102, 204], textColor: [255, 255, 255], fontStyle: "bold" },
      styles: { halign: "center", fontSize: 9 },
      didParseCell: function (data) {
        if (data.row.raw[1] === "Total") {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    doc.save("Vehicle_Deployment_Report.pdf");
  };

  return (
    <>
      <button className="btn btn-excel me-2" onClick={downloadAsPDF}>
        <i className="fa-solid fa-file-pdf me-1"></i> PDF
      </button>

      <button className="btn btn-excel" onClick={downloadAsExcel}>
        <i className="fa-solid fa-file-excel me-1"></i> Excel
      </button>
    </>
  );
};
